import styled from "styled-components";
import { motion } from "motion/react";
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from "react-zoom-pan-pinch";
import { Image } from "semantic-ui-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Guide } from "@agp/shared.user-app/entity";
import ServiceLocator from "../../../../../domain/service-locator";

interface LocalMapProps {
  guides: Guide[];
  mapImageUrl: string;
  pinPositions: Map<string, { x: number; y: number }>;
  pinColor: string;
}

export const MinpakuLocalMap = (props: LocalMapProps) => {
  /**
   * Prepare Modules
   */
  const playerLayoutModule = ServiceLocator.PlayerLayoutModule;
  const guidePlayerModule = ServiceLocator.GuidePlayerModule;

  /**
   * useRef
   */
  const transformComponent = useRef<ReactZoomPanPinchRef>(null!);

  /**
   * watch ReactiveProperty
   */
  const playerType = playerLayoutModule.playerType.watch();
  const playerHeight = playerLayoutModule.playerHeight.watch();
  const currentTrack = guidePlayerModule.currentTrack.watch();

  /**
   * useState
   */
  const [scale, setScale] = useState<number>(1);

  /**
   * useMemo
   */
  const pinScale = useMemo(() => Math.min(Math.max(1 / scale, 0), 1), [scale]);

  // ピン位置を持つガイドのみをフィルタ
  const guidesWithPins = useMemo(() => {
    return props.guides.filter((guide) => props.pinPositions.has(guide.id)).sort((a, b) => a.trackNumber - b.trackNumber);
  }, [props.guides, props.pinPositions]);

  const selectedGuideIndex = useMemo(() => {
    if (!currentTrack) return -1;
    return guidesWithPins.findIndex((x) => x.id === currentTrack.id);
  }, [guidesWithPins, currentTrack]);

  const bottomPlayerMargin = useMemo(
    () => (playerType === "full" ? 0 : playerHeight),
    [playerType, playerHeight]
  );

  /**
   * useCallback
   */
  const onZoomEnd = useCallback((scale: number) => setScale(scale), []);

  const onClickPin = useCallback(
    (index: number) => {
      guidePlayerModule.select(guidesWithPins[index].id);
      if (!currentTrack) playerLayoutModule.playerType.setValue("full");
    },
    [
      currentTrack,
      guidePlayerModule,
      playerLayoutModule.playerType,
      guidesWithPins,
    ]
  );

  return (
    <Wrapper
      animate={{ paddingBottom: bottomPlayerMargin }}
      transition={{
        type: "tween",
        duration: 0.3,
      }}
    >
      <div style={{ margin: "0 auto", width: "fit-content" }}>
        <TransformWrapper
          onWheelStop={(ref) => onZoomEnd(ref.state.scale)}
          onPinchingStop={(ref) => onZoomEnd(ref.state.scale)}
          ref={transformComponent}
        >
          <TransformComponent
            wrapperStyle={{
              overflow: "visible",
              height: "100%",
            }}
          >
            <Image
              src={props.mapImageUrl}
              style={{
                height: `calc(${window.innerHeight}px - 200px)`,
              }}
            />
            {guidesWithPins.map((guide, i) => {
              const position = props.pinPositions.get(guide.id);
              if (!position) return null;
              const isActive = i === selectedGuideIndex;
              return (
                <CircleWrapper
                  id={`circle-${guide.trackNumber}`}
                  key={`circle-${guide.trackNumber}`}
                  onClick={() => {
                    onClickPin(i);
                  }}
                  initial={{
                    x: "-50%",
                    y: "50%",
                  }}
                  animate={{
                    scale: pinScale * (isActive ? 0.7 : 0.5),
                    x: "-50%",
                    y: "50%",
                  }}
                  pinX={position.x}
                  pinY={1 - position.y}
                  isActive={isActive}
                  transition={{
                    type: "tween",
                    duration: 0.3,
                  }}
                >
                  <Circle isActive={isActive} pinColor={props.pinColor}>
                    <TrackNumber>{i}</TrackNumber>
                  </Circle>
                </CircleWrapper>
              );
            })}
          </TransformComponent>
        </TransformWrapper>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled(motion.div)`
  height: 100%;
  position: relative;
`;

const CircleWrapper = styled(motion.div)<{
  pinX: number;
  pinY: number;
  isActive: boolean;
}>`
  position: absolute;
  left: ${(props) => props.pinX * 100}%;
  bottom: ${(props) => props.pinY * 100}%;
  z-index: ${(props) => (props.isActive ? 1000 : 0)};
`;

const Circle = styled.div<{ isActive: boolean; pinColor: string }>`
  width: 60px;
  height: 60px;
  border-radius: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(props) => (props.isActive ? "#FFFFFF" : "#000000")};
  color: ${(props) => (props.isActive ? "#000000" : "#FFFFFF")};
  border: ${(props) => (props.isActive ? "3px solid #000000" : "none")};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
`;

const TrackNumber = styled.div`
  pointer-events: none;
  font-size: 2rem;
  font-weight: bold;
`;
